import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LandingHeader from '../Shared/LandingHeader';
import LandingFooter from '../Shared/LandingFooter';
import './AboutUs.css';

const AboutUs = () => {
    const { t } = useTranslation(['about', 'common']);

    const stats = [
        { value: '10,00+', label: t('about:stats.students') },
        { value: '2+', label: t('about:stats.schools') },
        { value: '95%', label: t('about:stats.improvement') },
        { value: '4.9★', label: t('about:stats.rating') },
    ];

    const teamMembers = t('about:team.members', { returnObjects: true }) || [];
    const teamWithEmojis = teamMembers.map((m, i) => ({
        ...m,
        emoji: ['👩‍💼', '👨‍💻', '👩‍🏫', '🎨'][i] || '👤'
    }));

    const values = t('about:values.items', { returnObjects: true }) || [];
    const valuesWithIcons = values.map((v, i) => ({
        ...v,
        icon: ['🧠', '🤝', '⚡', '🌍'][i] || '✨'
    }));

    return (
        <div className="about-page">
            <LandingHeader activePage="about" />

            <main style={{ paddingTop: '80px' }}>
                {/* Hero */}
                <section className="about-hero">
                    <div className="about-container">
                        <div className="about-hero-badge">{t('about:hero.badge')}</div>
                        <h1>
                            {t('about:hero.title')}<br />
                            <span className="about-gradient">{t('about:hero.titleHighlight')}</span><br />
                            {t('about:hero.titleSuffix')}
                        </h1>
                        <p className="about-hero-sub">
                            {t('about:hero.subtext')}
                        </p>
                    </div>
                    <div className="about-hero-glow" />
                </section>

                {/* Stats */}
                <section className="about-stats-section">
                    <div className="about-container">
                        <div className="about-stats-grid">
                            {stats.map((s, i) => (
                                <div className="about-stat-card" key={i}>
                                    <span className="about-stat-value">{s.value}</span>
                                    <span className="about-stat-label">{s.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Mission */}
                <section className="about-mission-section">
                    <div className="about-container about-two-col">
                        <div className="about-mission-content">
                            <span className="about-section-tag">{t('about:mission.tag')}</span>
                            <h2>{t('about:mission.title')}</h2>
                            <p>{t('about:mission.text1')}</p>
                            <p>{t('about:mission.text2')}</p>
                        </div>
                        <div className="about-mission-visual">
                            <div className="about-mission-card">
                                <div className="about-mission-icon">🔬</div>
                                <h3>{t('about:mission.cards.diagnose.title')}</h3>
                                <p>{t('about:mission.cards.diagnose.text')}</p>
                            </div>
                            <div className="about-mission-card">
                                <div className="about-mission-icon">🎯</div>
                                <h3>{t('about:mission.cards.target.title')}</h3>
                                <p>{t('about:mission.cards.target.text')}</p>
                            </div>
                            <div className="about-mission-card">
                                <div className="about-mission-icon">🚀</div>
                                <h3>{t('about:mission.cards.accelerate.title')}</h3>
                                <p>{t('about:mission.cards.accelerate.text')}</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Team */}
                <section className="about-team-section">
                    <div className="about-container">
                        <span className="about-section-tag">{t('about:team.tag')}</span>
                        <h2 className="about-section-title">{t('about:team.title')} <span className="about-gradient">{t('about:team.titleHighlight')}</span></h2>
                        <p className="about-section-sub">
                            {t('about:team.sub')}
                        </p>
                        <div className="about-team-grid">
                            {teamWithEmojis.map((member, i) => (
                                <div className="about-team-card" key={i}>
                                    <div className="about-team-avatar">{member.emoji}</div>
                                    <h3>{member.name}</h3>
                                    <span className="about-team-role">{member.role}</span>
                                    <p>{member.bio}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Values */}
                <section className="about-values-section">
                    <div className="about-container">
                        <span className="about-section-tag">{t('about:values.tag')}</span>
                        <h2 className="about-section-title">{t('about:values.title')} <span className="about-gradient">{t('about:values.titleHighlight')}</span></h2>
                        <div className="about-values-grid">
                            {valuesWithIcons.map((v, i) => (
                                <div className="about-value-card" key={i}>
                                    <div className="about-value-icon">{v.icon}</div>
                                    <h3>{v.title}</h3>
                                    <p>{v.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="about-cta-section">
                    <div className="about-container">
                        <h2>{t('about:cta.title')}</h2>
                        <p>{t('about:cta.text')}</p>
                        <div className="about-cta-btns">
                            <Link to="/register" className="about-btn-primary">{t('about:cta.buttonStart')}</Link>
                            <Link to="/pricing" className="about-btn-secondary">{t('about:cta.buttonPricing')}</Link>
                        </div>
                    </div>
                </section>
            </main>

            <LandingFooter />
        </div>
    );
};

export default AboutUs;
